import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from './utils';

export const UsersTab = ({ visible, refreshTrigger, currentUserId, authHeaders }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [pendingUserId, setPendingUserId] = useState(null);

  const loadUsers = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/users`, {
        headers: authHeaders(),
      });
      setUsers(res.data.users || []);
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.message ||
        'Failed to load users';
      setError(msg);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (visible) loadUsers();
  }, [visible, refreshTrigger, loadUsers]);

  const handleRoleChange = async (userId, newRole) => {
    setActionError(null);
    setPendingUserId(userId);
    try {
      await axios.patch(
        `${API_URL}/api/admin/users/${encodeURIComponent(userId)}/role`,
        { role: newRole },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
      );
      setUsers((prev) =>
        prev.map((u) => (u.user_id === userId ? { ...u, role: newRole } : u)),
      );
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.message ||
        'Failed to update role';
      setActionError(msg);
    } finally {
      setPendingUserId(null);
    }
  };

  const handleDelete = async (userId, displayName) => {
    if (userId === currentUserId) return;
    const ok = window.confirm(
      `Delete account for "${displayName}"? This cannot be undone.`,
    );
    if (!ok) return;

    setActionError(null);
    setPendingUserId(userId);
    try {
      await axios.delete(
        `${API_URL}/api/admin/users/${encodeURIComponent(userId)}`,
        { headers: authHeaders() },
      );
      setUsers((prev) => prev.filter((u) => u.user_id !== userId));
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.message ||
        'Failed to delete user';
      setActionError(msg);
    } finally {
      setPendingUserId(null);
    }
  };

  return (
    <>
      {error && (
        <div className="AdminPortal-banner AdminPortal-banner--error" role="alert">
          {error}
        </div>
      )}
      {actionError && (
        <div className="AdminPortal-banner AdminPortal-banner--error" role="alert">
          {actionError}
        </div>
      )}

      <div className="AdminPortal-toolbar">
        <button
          type="button"
          className="ChatButton"
          onClick={loadUsers}
          disabled={loading}
        >
          Refresh
        </button>
      </div>

      {loading ? (
        <p className="AdminPortal-intro">Loading users…</p>
      ) : (
        <div className="AdminPortal-tableWrap">
          <table className="AdminPortal-table">
            <thead>
              <tr>
                <th scope="col">User ID</th>
                <th scope="col">Username</th>
                <th scope="col">Role</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={4} className="AdminPortal-tableEmpty">
                    No users found.
                  </td>
                </tr>
              ) : (
                users.map((u) => {
                  const isSelf = u.user_id === currentUserId;
                  const busy = pendingUserId === u.user_id;
                  return (
                    <tr key={u.user_id}>
                      <td className="AdminPortal-mono">{u.user_id}</td>
                      <td>{u.username}</td>
                      <td>
                        <select
                          className="AdminPortal-select"
                          value={u.role}
                          disabled={busy || isSelf}
                          title={
                            isSelf
                              ? 'You cannot change your own role here'
                              : undefined
                          }
                          onChange={(e) =>
                            handleRoleChange(u.user_id, e.target.value)
                          }
                          aria-label={`Role for ${u.username}`}
                        >
                          <option value="user">user</option>
                          <option value="admin">admin</option>
                        </select>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="ChatButton AdminPortal-delete"
                          disabled={busy || isSelf}
                          title={isSelf ? 'You cannot delete your own account' : undefined}
                          onClick={() => handleDelete(u.user_id, u.username)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};
